import React, { useRef } from "react";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

const utahData = "/img/UTDT_Logo.png";
const bodex = "/img/bodexLogo.png";
const kpi = "/img/KPI-DASH 2.png";
const pdfIcon = "/img/pdf1.png";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
);

export default function ConstructionTable12({ data = [] }) {
  const pdfRef = useRef();

  if (!Array.isArray(data) || data.length === 0) {
    return <p>No data available</p>;
  }

  // Prepare chart data
  const labels = data.map((item) => item.age_group_label);
  const femaleValues = data.map((item) => parseFloat(item.female_percent || 0));
  const maleValues = data.map((item) => parseFloat(item.male_percent || 0));

  const chartData = {
    labels,
    datasets: [
      {
        label: "Female (%)",
        data: femaleValues,
        backgroundColor: "#EC4899", // pink
        borderRadius: 6,
      },
      {
        label: "Male (%)",
        data: maleValues,
        backgroundColor: "#0ea5e9", // blue
        borderRadius: 6,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: "top" },
      title: {
        display: true,
        text: "👩‍💼 Male vs Female Employment Percentage by Age Group (2024)",
        font: { size: 16, weight: "bold" },
      },
      tooltip: {
        callbacks: {
          label: function (context) {
            return `${context.dataset.label}: ${context.raw.toFixed(2)}%`;
          },
        },
      },
    },
    scales: {
      x: {
        title: { display: true, text: "Age Group" },
        grid: { display: false },
      },
      y: {
        beginAtZero: true,
        max: 100,
        title: { display: true, text: "Percentage (%)" },
      },
    },
  };

  // Summary Insights
  const avgFemale = (
    femaleValues.reduce((a, b) => a + b, 0) / femaleValues.length
  ).toFixed(1);
  const avgMale = (
    maleValues.reduce((a, b) => a + b, 0) / maleValues.length
  ).toFixed(1);

  const topFemale = data.reduce(
    (max, item) =>
      parseFloat(item.female_percent || 0) > max.female_percent
        ? {
            label: item.age_group_label,
            female_percent: parseFloat(item.female_percent),
          }
        : max,
    {
      label: data[0].age_group_label,
      female_percent: parseFloat(data[0].female_percent || 0),
    },
  );

  const topMale = data.reduce(
    (max, item) =>
      parseFloat(item.male_percent || 0) > max.male_percent
        ? {
            label: item.age_group_label,
            male_percent: parseFloat(item.male_percent),
          }
        : max,
    {
      label: data[0].age_group_label,
      male_percent: parseFloat(data[0].male_percent || 0),
    },
  );

  // PDF Download
  const downloadPDF = () => {
    const input = pdfRef.current;
    html2canvas(input, {
      scale: 2,
      useCORS: true,
      backgroundColor: "#ffffff",
      scrollY: -window.scrollY,
    }).then((canvas) => {
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = pageWidth - 10;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      let heightLeft = imgHeight;
      let position = 0;
      let pageNumber = 1;

      const addHeader = () => {
        const logoWidth = 20; // ideal header width
        const logoHeight = 20; // adjust ratio if needed

        pdf.addImage(utahData, "PNG", 70, 10, logoWidth, logoHeight);
        pdf.setFontSize(26);
        pdf.text("UTahData.org", 95, 22);
        pdf.link(50, 15, 60, 10, { url: "https://utahdata.org" });
        pdf.line(10, 35, 200, 35);
      };

      const addFooter = (pageNumber) => {
        pdf.setFontSize(9);
        const footerY = pageHeight - 10;
        pdf.line(10, footerY - 5, pageWidth - 10, footerY - 5);
        pdf.text(
          `Downloaded on: ${new Date().toLocaleDateString()}`,
          10,
          footerY,
        );
        pdf.text(
          `Page ${pageNumber}`,
          pageWidth - 10,
          footerY,
          null,
          null,
          "right",
        );
      };

      // First page
      addHeader();
      pdf.addImage(imgData, "PNG", 5, 32, imgWidth, imgHeight);
      heightLeft -= pageHeight - 32;

      // Add more pages if needed
      while (heightLeft > 0) {
        pdf.addPage();
        pageNumber++;
        addHeader();
        position = heightLeft - imgHeight;
        pdf.addImage(imgData, "PNG", 5, position + 32, imgWidth, imgHeight);
        heightLeft -= pageHeight - 32;
        addFooter(pageNumber);
      }

      // Final footer
      // ===== FINAL FOOTER SAFE AREA =====

      const footerTopY = pageHeight - 40; // start footer 40mm from bottom
      const footerContentY = pageHeight - 28; // logos row
      const footerBottomTextY = pageHeight - 10; // bottom text row

      // Top Divider Line
      pdf.line(10, footerTopY, pageWidth - 10, footerTopY);

      // UtahData logo
      const utahWidth = 18;
      const utahHeight = 18;
      pdf.addImage(
        utahData,
        "PNG",
        20,
        footerContentY - 8,
        utahWidth,
        utahHeight,
      );

      // Built by
      pdf.setFontSize(10);
      pdf.text("Built by", 45, footerContentY);

      // BODEX logo
      const bodexWidth = 36;
      const bodexHeight = bodexWidth * 0.3;
      pdf.addImage(
        bodex,
        "PNG",
        60,
        footerContentY - 6,
        bodexWidth,
        bodexHeight,
      );

      // Using text
      pdf.text("using", 102, footerContentY);

      // KPI logo
      pdf.addImage(kpi, "PNG", 118, footerContentY - 8, 16, 16);

      // Version
      pdf.text("20250916.v3.198", 140, footerContentY);

      // Bottom Info Line
      pdf.setFontSize(8);
      pdf.text(`From: ${window.location.href}`, 10, footerBottomTextY);
      pdf.text(
        `Downloaded on: ${new Date().toLocaleDateString()}`,
        pageWidth - 10,
        footerBottomTextY,
        null,
        null,
        "right",
      );

      pdf.save("employment_percentage_age_group_2024.pdf");
    });
  };

  return (
    <>
      <div
        ref={pdfRef}
        className="p-6 bg-white from-blue-50 via-white to-pink-50 rounded-xl shadow-md"
      >
        <h2 className="text-2xl font-bold mb-6 text-center text-gray-800">
          Male vs Female Employment Percentage by Age Group (2024)
        </h2>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6 text-center">
          <div className="p-4 bg-pink-100 rounded-xl shadow">
            <p className="text-sm text-gray-600 font-medium">
              Average Female Participation
            </p>
            <p className="text-xl font-bold text-pink-800">{avgFemale}%</p>
          </div>
          <div className="p-4 bg-blue-100 rounded-xl shadow">
            <p className="text-sm text-gray-600 font-medium">
              Average Male Participation
            </p>
            <p className="text-xl font-bold text-blue-800">{avgMale}%</p>
          </div>
          <div className="p-4 bg-yellow-100 rounded-xl shadow">
            <p className="text-sm text-gray-600 font-medium">
              Highest Female Share
            </p>
            <p className="text-base font-semibold text-yellow-800">
              {topFemale.label}: {topFemale.female_percent}%
            </p>
          </div>
        </div>

        {/* Table + Chart Layout */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {/* Table */}
          <div className="flex flex-col h-full">
            <div className="overflow-x-auto flex-grow">
              <table className="w-full border-collapse text-center shadow-md rounded-lg overflow-hidden">
                <thead className="bg-gray-800 text-white">
                  <tr>
                    <th className="p-2 text-left">Age Group</th>
                    <th className="p-2" style={{ textAlign: "right" }}>
                      Female (%)
                    </th>
                    <th className="p-2" style={{ textAlign: "right" }}>
                      Male (%)
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {data.map((row, idx) => (
                    <tr
                      key={idx}
                      className={`${
                        row.age_group_label === topFemale.label
                          ? "bg-yellow-50 font-semibold"
                          : "hover:bg-gray-100"
                      }`}
                    >
                      <td className="p-2 text-left">{row.age_group_label}</td>
                      <td
                        className="p-2"
                        style={{
                          textAlign: "right",
                          fontVariantNumeric: "tabular-nums",
                        }}
                      >
                        {row.female_percent}%
                      </td>
                      <td
                        className="p-2"
                        style={{
                          textAlign: "right",
                          fontVariantNumeric: "tabular-nums",
                        }}
                      >
                        {row.male_percent}%
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Chart */}
          <div className="flex flex-col w-full overflow-x-auto bg-white rounded-lg shadow-md">
            <div className="min-w-[400px] w-full h-96 p-4">
              <Bar data={chartData} options={options} />
            </div>
          </div>
        </div>
      </div>

      {/* Download Button */}
      <div className="flex justify-center mt-4">
        <button
          onClick={downloadPDF}
          className="bg-red-700 hover:bg-red-900 px-4 py-2 text-white rounded-lg flex items-center gap-2 shadow-lg transition-transform hover:scale-105"
        >
          <span>Download as PDF</span>
          <img className="w-[1.5rem]" src={pdfIcon} alt="pdf-icon" />
        </button>
      </div>
    </>
  );
}
